import React, { useState, useEffect, memo } from "react";
import { Dropdown, Icon } from "semantic-ui-react";
import {
  DeviceFiltersOptionsType,
  FilterRow,
  FilterState,
  HeartbeatField,
  StyledDropdown,
} from "./DevicesFilterUI";
import {
  ClickhouseToTSTypes,
  getOperatorsOptionsByDataType,
} from "../../../../../util";
import { queryOperators } from "../../../Dashboards/util";
import DevicesFilterSearchInput, {
  StyledInput,
} from "../../../../common/StyledComponents/SearchInput";
import { StyledButtonWithBorder } from "../Devices";
import MultiSelectDropdown from "./MultiSelectDropdown";

export const HeartbeatFilterOptions = [
  {
    key: "few_seconds",
    text: "few seconds",
    value: 60, // 60 seconds
  },
  {
    key: "5_minutes",
    text: "5 minutes",
    value: 5 * 60, // 5 minutes in seconds
  },
  {
    key: "1_hour",
    text: "1 hour",
    value: 60 * 60, // 1 hour in seconds
  },
  {
    key: "1_day",
    text: "1 day",
    value: 24 * 60 * 60, // 1 day in seconds
  },
  {
    key: "1_week",
    text: "1 week",
    value: 7 * 24 * 60 * 60, // 1 week in seconds
  },
  {
    key: "1_month",
    text: "1 month",
    value: 30 * 24 * 60 * 60, // 30 days in seconds
  },
  {
    key: "1_year",
    text: "1 year",
    value: 365 * 24 * 60 * 60, // 365 days in seconds
  },
  {
    key: "custom",
    text: "Custom",
    value: null, // Will be set based on user input
    isCustom: true,
  },
];

// Time unit options for custom time selection
const TimeUnitOptions = [
  { key: "seconds", text: "Seconds", value: "seconds" },
  { key: "minutes", text: "Minutes", value: "minutes" },
  { key: "hours", text: "Hours", value: "hours" },
  { key: "days", text: "Days", value: "days" },
  { key: "weeks", text: "Weeks", value: "weeks" },
  { key: "months", text: "Months", value: "months" },
  { key: "years", text: "Years", value: "years" },
];

interface EditableFilterRowProps {
  filters: FilterState[];
  filter: FilterState;
  fieldsOptions: DeviceFiltersOptionsType[];
  filterOptions: { [key: string]: string[] };
  updateFilter: (
    id: string,
    updatedFilter: FilterState,
    refresh: boolean
  ) => void;
  removeFilter: (id: string) => void;
  getDeviceShadowFieldType: (field: string) => ClickhouseToTSTypes;
  getDeviceShadowFieldTypeNullable: (field: string) => boolean;
}

const EditableFilterRow: React.FC<EditableFilterRowProps> = ({
  filters,
  filter,
  fieldsOptions,
  updateFilter,
  removeFilter,
  getDeviceShadowFieldType,
  getDeviceShadowFieldTypeNullable,
  filterOptions,
}) => {
  const [localFilter, setLocalFilter] = useState<FilterState>({ ...filter });
  const [showCustomTimeInput, setShowCustomTimeInput] = useState<boolean>(
    filter.field === HeartbeatField && filter.value === "custom"
  );

  // Set default field on component mount if no field is selected
  // Consider wrapping updateFilter and getDeviceShadowFieldType with useCallback in the parent component
  useEffect(() => {
    if (!localFilter.field && fieldsOptions.length > 0) {
      // Try to find "Device id" field first, otherwise use the first option
      const deviceIdField = fieldsOptions.find(
        (option) => option.passingvalue === "id"
      );

      const defaultField = deviceIdField || fieldsOptions[0];

      if (defaultField) {
        const splitValue = defaultField.value.split(":");
        const fieldType = (splitValue[0] as "metadata" | "state") || "metadata";
        const fieldValue = splitValue[1] || "";

        const isHeartbeatField = fieldValue === HeartbeatField;
        let operator = "=";
        if (isHeartbeatField) {
          operator = "in last";
        }

        const updatedFilter = {
          ...localFilter,
          field: fieldValue,
          columnType: getDeviceShadowFieldType(fieldValue),
          type: fieldType,
          operator,
          value: "",
          isNullOperator: false,
        };

        setLocalFilter(updatedFilter);
        updateFilter(filter.id, updatedFilter, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilter.field, filter.id]);

  // Format the field and type to correctly display the value in the dropdown
  const formatFieldValue = (
    filterType: FilterState["type"],
    filterField: FilterState["field"]
  ): DeviceFiltersOptionsType["value"] => {
    return `${filterType}:${filterField}`;
  };

  // Handle field change
  const handleFieldChange = (_, data) => {
    // Split the value to get the field and type, e.g. "metadata:id" -> ["metadata", "id"]
    const [fieldType, fieldValue]: ["metadata" | "state", string] =
      data.value.split(":");

    const isHeartbeatField = fieldValue === HeartbeatField;
    if (localFilter.field !== fieldValue) {
      setShowCustomTimeInput(false);
    }

    let operator = "=";
    if (isHeartbeatField) {
      operator = "in last";
    }

    const updatedFilter = {
      ...localFilter,
      field: fieldValue,
      columnType: getDeviceShadowFieldType(fieldValue),
      type: fieldType,
      operator,
      value: "",
      isNullOperator: false,
    };

    setLocalFilter(updatedFilter);
    updateFilter(filter.id, updatedFilter, false);
  };

  // Handle operator change
  const handleOperatorChange = (_, data) => {
    if (localFilter.field === HeartbeatField) {
      const updatedFilter = {
        ...localFilter,
        operator: data.value,
        value: null,
      };
      setLocalFilter(updatedFilter);
      updateFilter(filter.id, updatedFilter, false);
      return;
    }

    let updatedFilter;
    if (
      (data.value === "=" || data.value === "not=") &&
      localFilter.columnType === "boolean"
    ) {
      // Case I for boolean type
      updatedFilter = {
        ...localFilter,
        operator: data.value,
        isNullOperator: false,
        columnType: "boolean",
        value: "",
      };
    } else if (data.value === "IS NULL" || data.value === "IS NOT NULL") {
      // Case II for null operator
      updatedFilter = {
        ...localFilter,
        columnType: "null",
        operator: data.value === "IS NULL" ? "=" : "not=",
        isNullOperator: true,
        value: "null",
      };
    } else {
      // Case III for other operators
      let columnType = getDeviceShadowFieldType(localFilter.field);
      if (
        localFilter.columnType === "null" &&
        localFilter.type === "metadata"
      ) {
        columnType = "int";
      }

      updatedFilter = {
        ...localFilter,
        operator: data.value,
        isNullOperator: false,
        columnType,
        value: "",
      };
    }

    setLocalFilter(updatedFilter);
    updateFilter(
      filter.id,
      updatedFilter,
      data.value === "IS NULL" || data.value === "IS NOT NULL" // refresh if null operator is selected since value is changed
    );
  };

  const handleMetadataValueChange = (data) => {
    const updatedFilter = {
      ...localFilter,
      value: data,
    };
    setLocalFilter(updatedFilter);
    updateFilter(filter.id, updatedFilter, true);
  };

  const handleValueChange = (e) => {
    const updatedFilter = {
      ...localFilter,
      value: e.target.value.toString(),
    };
    setLocalFilter(updatedFilter);
    updateFilter(filter.id, updatedFilter, true);
  };

  // Get operator options based on field type
  const getOperatorOptions = () => {
    let operatorOptions: {
      key: string;
      value: string;
      text: string;
      content?: React.JSX.Element;
    }[] = [];

    if (localFilter.field === HeartbeatField) {
      operatorOptions = [{ key: "in last", value: "in last", text: "in last" }];
    } else if (localFilter.field === "id") {
      operatorOptions = [{ key: "contains", value: "=", text: "contains" }];
    } else if (localFilter.type !== "metadata") {
      operatorOptions = getOperatorsOptionsByDataType(
        queryOperators,
        getDeviceShadowFieldType(localFilter.field),
        getDeviceShadowFieldTypeNullable(localFilter.field)
      );
    } else {
      operatorOptions = [
        { key: "contains", value: "=", text: "contains" },
        { key: "IS NULL", value: "IS NULL", text: "IS NULL" },
        { key: "IS NOT NULL", value: "IS NOT NULL", text: "IS NOT NULL" },
      ];
    }

    return operatorOptions;
  };

  // Handle custom time input for heartbeat field
  const handleCustomTimeValueChange = (e) => {
    if (e.target.value === "") {
      const updatedFilter = {
        ...localFilter,
        customTimeValue: undefined,
      };
      setLocalFilter(updatedFilter);
      updateFilter(filter.id, updatedFilter, true);
    } else {
      const value = parseInt(e.target.value);
      if (!isNaN(value) && value >= 0) {
        const updatedFilter = {
          ...localFilter,
          customTimeValue: value,
        };
        setLocalFilter(updatedFilter);
        updateFilter(filter.id, updatedFilter, true);
      }
    }
  };

  // Handle custom time unit change
  const handleCustomTimeUnitChange = (_, data) => {
    const updatedFilter = {
      ...localFilter,
      customTimeUnit: data.value as string,
    };
    setLocalFilter(updatedFilter);
    updateFilter(filter.id, updatedFilter, true);
  };

  // Handle heartbeat value change
  const handleHeartbeatValueChange = (_, data) => {
    if (data.value === "custom") {
      setShowCustomTimeInput(true);
      const updatedFilter = {
        ...localFilter,
        value: "custom",
        customTimeValue: undefined,
        customTimeUnit: "minutes",
      };
      setLocalFilter(updatedFilter);
      updateFilter(filter.id, updatedFilter, true);
    } else {
      setShowCustomTimeInput(false);
      const updatedFilter = {
        ...localFilter,
        value: data.value as string,
        customTimeValue: undefined,
        customTimeUnit: undefined,
      };
      setLocalFilter(updatedFilter);
      updateFilter(filter.id, updatedFilter, true);
    }
  };

  // Handle boolean value change
  const handleBooleanValueChange = (_, data) => {
    const updatedFilter = {
      ...localFilter,
      value: data.value as string,
    };
    setLocalFilter(updatedFilter);
    updateFilter(filter.id, updatedFilter, true);
  };

  // Clear the input value and reset the current filter value.
  const handleClearInputClick = () => {
    setLocalFilter({ ...localFilter, value: "" });
  };

  // Modify renderValueField to show multiselect for metadata
  const renderValueField = () => {
    if (localFilter.field === HeartbeatField) {
      if (!showCustomTimeInput) {
        return (
          <Dropdown
            style={{
              width: "40%",
              height: "32px",
              minHeight: "32px",
              fontSize: "13px",
            }}
            placeholder="Select time range..."
            selection
            fluid
            options={HeartbeatFilterOptions.map((option) => ({
              key: option.key,
              text: option.text,
              value: option.key,
            }))}
            value={localFilter.value || ""}
            onChange={handleHeartbeatValueChange}
          />
        );
      } else {
        return (
          <StyledInput
            type="number"
            min="0"
            style={{
              width: "40%",
              height: "32px",
              minHeight: "32px",
              fontSize: "13px",
            }}
            inputWidth="calc(100% - 124px)"
            background="transparent"
            placeholder={`Enter time in`}
            value={localFilter.customTimeValue ?? ""}
            onChange={handleCustomTimeValueChange}
            label={
              <Dropdown
                style={{
                  minWidth: "124px",
                  width: "124px",
                  height: "30px",
                  minHeight: "30px",
                  fontSize: "13px",
                }}
                selection
                options={TimeUnitOptions}
                value={localFilter.customTimeUnit ?? "minutes"}
                onChange={handleCustomTimeUnitChange}
              />
            }
            labelPosition="right"
          />
        );
      }
    } else if (localFilter.columnType === "boolean") {
      return (
        <Dropdown
          style={{
            width: "40%",
            height: "32px",
            minHeight: "32px",
            fontSize: "13px",
          }}
          placeholder="Select value..."
          selection
          fluid
          options={[
            { key: "true", text: "true", value: "true" },
            { key: "false", text: "false", value: "false" },
          ]}
          value={localFilter.value || ""}
          onChange={handleBooleanValueChange}
        />
      );
    } else if (
      (localFilter.type === "metadata" || localFilter.field === "id") &&
      filterOptions[localFilter.field] &&
      !localFilter.isNullOperator
    ) {
      return (
        <MultiSelectDropdown
          style={{
            width: "40%",
            height: "32px",
            minHeight: "32px",
            fontSize: "13px",
          }}
          placeholder={`Select ${fieldsOptions?.find((f) => f.passingvalue === localFilter.field)?.text}...`}
          options={filterOptions[localFilter.field].map((value) => ({
            key: value,
            text: value,
            value: String(value),
          }))}
          value={Array.isArray(localFilter.value) ? 
            localFilter.value
              .map((v) => String(v))
              : []}
          onChange={(data) => {
            handleMetadataValueChange(data);
          }}
        />
      );
    } else {
      return (
        <DevicesFilterSearchInput
          name="search_input"
          style={{
            width: "40%",
            height: "32px",
            minHeight: "32px",
            fontSize: "13px",
          }}
          background="transparent"
          type={"text"}
          icon={
            localFilter?.value
              ? {
                  name: "close",
                  link: true,
                  onClick: handleClearInputClick,
                  disabled: localFilter.isNullOperator,
                }
              : undefined
          }
          value={localFilter.value || ""}
          placeholder={
            localFilter.field
              ? `Find Device by ${localFilter.field}...`
              : "Find Devices..."
          }
          onChange={handleValueChange}
          disabled={localFilter.isNullOperator}
        />
      );
    }
  };

  return (
    <FilterRow style={{ gap: "0px" }}>
      <FilterRow style={{ margin: "0px" }}>
        <StyledDropdown
          // disabled={!isNewFilter}
          style={{
            width: "45%",
            height: "32px",
            minHeight: "32px",
            fontSize: "13px",
          }}
          placeholder="Select Field"
          search
          fluid
          selection
          options={fieldsOptions}
          value={formatFieldValue(localFilter.type, localFilter.field)}
          onChange={handleFieldChange}
        />

        <Dropdown
          key={localFilter.operator}
          disabled={
            localFilter.field === HeartbeatField ||
            localFilter.field === "id" ||
            !localFilter.field
          }
          style={{
            width: "10%",
            minWidth: "120px",
            height: "32px",
            minHeight: "32px",
            fontSize: "13px",
          }}
          placeholder="Operator"
          selection
          options={getOperatorOptions()}
          value={(() => {
            if (localFilter.isNullOperator) {
              return localFilter.operator === "=" ? "IS NULL" : "IS NOT NULL";
            }
            return localFilter.operator;
          })()}
          onChange={handleOperatorChange}
        />

        {renderValueField()}
      </FilterRow>
      <StyledButtonWithBorder
        style={{
          padding: "8px",
          margin: "0px",
          marginLeft: "8px",
          width: "32px",
          height: "32px",
        }}
        onClick={() => {
          if (filters.length > 1) removeFilter(filter.id);
        }}
        disabled={filters.length <= 1}
      >
        <Icon name="close" style={{ fontSize: "14px", margin: 0 }} />
      </StyledButtonWithBorder>
    </FilterRow>
  );
};

export default memo(EditableFilterRow);
